// item-titles.js — title resolver for lesson-item rows.
//
// The Unity port's `kfe_content_items.json` has ~9,300 items (~39%) with
// empty `titleEn` AND `titleZh`. Live testing on 2026-05-25 surfaced those
// as `Item item_<id>` rows — the bare ID with a stub label. The iOS plists
// carry full bilingual titles for those items; this resolver fills the gap
// by consulting a tracked side-channel map (`content/external-titles.json`)
// extracted by `tools/extract-plist-titles.mjs`.
//
// Resolution order (Brief 038):
//   1. The item's own titleZh (zh-Hans speaker) or titleEn.
//   2. The plist-sourced fallback from external-titles.json.
//   3. The bare filename stem (e.g. CJLAUD02.m4a → "CJLAUD02").
//   4. A per-type generic label sourced from i18n keys
//      (`itemType.haai`, `.l2bTraining`, `.l2bTesting`, `.selfRecording`).
//   5. `Item ${itemId}` as the last-resort label.
//
// The map is loaded once at boot via loadExternalTitles(); resolveItemTitle()
// then queries it synchronously. Mirrors the loader pattern in media-paths.js
// for external-media.json.

import { t, getLanguage } from "../core/i18n.js";
import {
  ITEM_TYPE_HAAI,
  ITEM_TYPE_L2B_TRAINING,
  ITEM_TYPE_L2B_TESTING,
  ITEM_TYPE_SELF_RECORDING,
} from "./content-items.js";

const CONTENT_BASE = "/content";

/**
 * @typedef {object} ExternalTitleRecord
 * @property {string} [titleEn]
 * @property {string} [titleZh]
 * @property {string} [descEn]
 * @property {string} [descZh]
 *
 * @typedef {object} ItemLike
 * @property {number|string}        [itemId]
 * @property {number|string}        [id]
 * @property {number|string}        [type]   numeric from kfe_content_items.json,
 *                                            or string from pathway lesson JSON.
 * @property {string}               [category] CMS activity Category letter (B–L);
 *                                            K = New Word (Brief 109).
 * @property {string}               [titleEn]
 * @property {string}               [titleZh]
 * @property {string|null}          [file1Name]
 * @property {string|null}          [file2Name]
 */

// Pathway lesson JSONs use string types (e.g. "haai_exercise") while
// kfe_content_items.json uses numeric ones (e.g. 7). Map the strings that
// matter for per-type labels back to their numeric equivalents so the
// switch below works for both shapes.
const TYPE_STRING_TO_NUMERIC = /** @type {const} */ ({
  haai_exercise:      ITEM_TYPE_HAAI,
  l2b_exercise:       ITEM_TYPE_L2B_TRAINING,
  l2b_test:           ITEM_TYPE_L2B_TESTING,
  recording_exercise: ITEM_TYPE_SELF_RECORDING,
});

/** @type {Record<string, ExternalTitleRecord> | null} */
let externalTitles = null;
/** @type {Promise<void> | null} */
let externalTitlesPromise = null;

/**
 * Synchronously inject the external-titles map (test seam / non-fetch boot).
 * @param {Record<string, ExternalTitleRecord> | null} m
 */
export function setExternalTitles(m) {
  externalTitles = m;
  externalTitlesPromise = Promise.resolve();
}

/**
 * Fetch content/external-titles.json once and cache it. Safe to call
 * repeatedly — subsequent calls return the same promise. If the file is
 * missing or fails to parse, the map is set to {} and resolveItemTitle()
 * falls through to its filename / per-type / Item-${id} fallbacks for every
 * item.
 * @returns {Promise<void>}
 */
export function loadExternalTitles() {
  if (externalTitlesPromise) return externalTitlesPromise;
  externalTitlesPromise = fetch(`${CONTENT_BASE}/external-titles.json`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((m) => {
      externalTitles = (m && typeof m === "object")
        ? /** @type {Record<string, ExternalTitleRecord>} */ (m)
        : {};
    });
  return externalTitlesPromise;
}

/**
 * Best-effort title for a single lesson-item row.
 *
 * @param {ItemLike} item
 * @returns {string}
 */
export function resolveItemTitle(item) {
  const lang = getLanguage();

  // 1. The catalog's own title.
  if (lang === "zh-Hans" && item.titleZh) return item.titleZh;
  if (item.titleEn) return item.titleEn;
  if (item.titleZh) return item.titleZh;

  // 2. Plist-sourced title (iOS-canonical label).
  const id = normaliseId(item.itemId ?? item.id);
  if (externalTitles && id != null) {
    const ext = externalTitles[id];
    if (ext) {
      if (lang === "zh-Hans" && ext.titleZh) return ext.titleZh;
      if (ext.titleEn) return ext.titleEn;
      if (ext.titleZh) return ext.titleZh;
    }
  }

  // 3. Bare filename stem.
  const stem = stemOf(item.file1Name) || stemOf(item.file2Name);
  if (stem) return stem;

  // 4. Per-type generic label, sourced from i18n keys.
  const typed = perTypeLabel(item.type);
  if (typed) return typed;

  // 5. Last-resort label.
  return id ? `Item ${id}` : "Item";
}

/**
 * Detect which stage of the resolveItemTitle chain wins for a given item.
 * Used by the content validator (`tests/content/validators/decoder-coverage`)
 * to count items per stage and flag the ones that fall all the way through
 * to the `Item ${id}` final fallback. Mirrors `resolveItemTitle` step-for-step
 * but returns the stage name instead of the resolved string so the validator
 * can run without an i18n bundle loaded.
 *
 * @param {ItemLike} item
 * @param {{ externalTitles?: Record<string, ExternalTitleRecord> | null }} [opts]
 * @returns {"catalog"|"external"|"stem"|"per-type"|"item-id"}
 */
export function resolveItemTitleStage(item, opts = {}) {
  const ext = opts.externalTitles ?? externalTitles ?? {};

  // 1. Catalog title.
  if (item && (item.titleEn || item.titleZh)) return "catalog";

  // 2. Plist-sourced external title.
  const id = normaliseId(item?.itemId ?? item?.id);
  if (id != null) {
    const e = ext[id];
    if (e && (e.titleEn || e.titleZh)) return "external";
  }

  // 3. Bare filename stem.
  if (stemOf(item?.file1Name) || stemOf(item?.file2Name)) return "stem";

  // 4. Per-type generic label.
  if (hasPerTypeLabel(item?.type)) return "per-type";

  // 5. Last-resort `Item ${id}` label.
  return "item-id";
}

/**
 * Description companion to resolveItemTitle. Lesson rows don't surface
 * descriptions today, but exercise screens (audio / video / html) may want
 * the plist's Description.{en,zh} when the catalog's descEn/descZh are
 * blank. Same fallback chain, minus the per-type label.
 *
 * @param {ItemLike & { descEn?: string, descZh?: string }} item
 * @returns {string}
 */
export function resolveItemDescription(item) {
  const lang = getLanguage();
  if (lang === "zh-Hans" && item.descZh) return item.descZh;
  if (item.descEn) return item.descEn;
  if (item.descZh) return item.descZh;

  const id = normaliseId(item.itemId ?? item.id);
  if (externalTitles && id != null) {
    const ext = externalTitles[id];
    if (ext) {
      if (lang === "zh-Hans" && ext.descZh) return ext.descZh;
      if (ext.descEn) return ext.descEn;
      if (ext.descZh) return ext.descZh;
    }
  }
  return "";
}

/**
 * Subtitle (line 2) for a lesson-item row (Brief 099 §4). iOS shows the
 * human-readable type description below the title. The canonical source is the
 * item's own description — `descEn` / `descZh` carry the exact iOS strings
 * ("FaceFonics™ - (English Subtitles)", "Kungfu English Reference Note",
 * "Situation Dialogue BrainSoaking Exercise", …). Exercise types ship with an
 * empty description, so we fall back to a per-type label.
 *
 *   1. resolveItemDescription(item)  — descEn/descZh (catalog, then plist).
 *   2. Category K (New Word) label   — single-word items ship empty desc and
 *                                      type 12 (shared with Situation Dialogue),
 *                                      so the per-type fallback would mislabel
 *                                      them "Situation Dialogue" (Brief 109).
 *   3. per-type i18n label           — exercises (empty desc) only.
 *   4. "" (no subtitle)              — caller hides the second line.
 *
 * @param {ItemLike & { descEn?: string, descZh?: string }} item
 * @returns {string}
 */
export function resolveItemSubtitle(item) {
  const desc = resolveItemDescription(item);
  if (desc) return desc;
  // Brief 109: Category K (New Word) before the per-type fallback. Uses the
  // canonical "New Word" label shared with the Lesson Report bar chart
  // (Brief 098 `lessonReport.catNewWord`).
  if (String(item?.category || "").toUpperCase() === "K") return t("lessonReport.catNewWord");
  const key = perTypeSubtitleKey(item?.type);
  return key ? t(key) : "";
}

/**
 * Map an item type (string from pathway JSON or numeric from the catalog) to
 * the i18n key for its fallback subtitle. Reuses the existing `itemType.*`
 * labels where they exist; adds Brief 099's three for MC / TF / dialogue.
 * @param {number|string|undefined} type
 * @returns {string|null}
 */
function perTypeSubtitleKey(type) {
  switch (type) {
    case "haai_exercise":      case ITEM_TYPE_HAAI:           return "itemType.haai";
    case "l2b_exercise":       case ITEM_TYPE_L2B_TRAINING:   return "itemType.l2bTraining";
    case "l2b_test":           case ITEM_TYPE_L2B_TESTING:    return "itemType.l2bTesting";
    case "recording_exercise": case ITEM_TYPE_SELF_RECORDING: return "itemType.selfRecording";
    case "multiple_choice":                                   return "lessonDetail.itemType.multipleChoice";
    case "true_false":                                        return "lessonDetail.itemType.trueFalse";
    case "situation_dialogue":                                return "lessonDetail.itemType.situationDialogue";
    default:                                                  return null;
  }
}

/**
 * Pathway lesson JSONs key items as "item_106802" / "gate_104822". The
 * external-titles map and kfe_content_items.json key by bare numeric IDs.
 * Strip a leading "item_" / "gate_" / "intro_" prefix and return the numeric
 * part, or null if nothing usable remains.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string|null}
 */
function normaliseId(raw) {
  if (raw == null) return null;
  const s = String(raw).replace(/^(item_|gate_|intro_)/, "");
  return /^\d+$/.test(s) ? s : (s || null);
}

/** @param {string|null|undefined} filename */
function stemOf(filename) {
  if (!filename || typeof filename !== "string") return "";
  return filename.replace(/\.[^.]+$/, "");
}

/**
 * Test-only: does the given type have a per-type generic label? Used by
 * resolveItemTitleStage to decide between the "per-type" and "item-id"
 * stages without needing an i18n bundle loaded.
 * @param {number|string|undefined} type
 */
function hasPerTypeLabel(type) {
  const n = typeof type === "string"
    ? (TYPE_STRING_TO_NUMERIC[/** @type {keyof typeof TYPE_STRING_TO_NUMERIC} */ (type)] ?? null)
    : (typeof type === "number" ? type : null);
  return (
    n === ITEM_TYPE_HAAI ||
    n === ITEM_TYPE_L2B_TRAINING ||
    n === ITEM_TYPE_L2B_TESTING ||
    n === ITEM_TYPE_SELF_RECORDING
  );
}

/** @param {number|string|undefined} type */
function perTypeLabel(type) {
  // Brief 038, content-owner-confirmed labels live in i18n under itemType.*.
  // Missing keys fall through t()'s en → key chain; for L2B Testing the
  // zh-Hans string is intentionally absent (escalated via title-todo.json)
  // so Chinese learners see the English fallback rather than an invented
  // translation.
  const numericType = typeof type === "string"
    ? (TYPE_STRING_TO_NUMERIC[/** @type {keyof typeof TYPE_STRING_TO_NUMERIC} */ (type)] ?? null)
    : (typeof type === "number" ? type : null);
  switch (numericType) {
    case ITEM_TYPE_HAAI:           return t("itemType.haai");
    case ITEM_TYPE_L2B_TRAINING:   return t("itemType.l2bTraining");
    case ITEM_TYPE_L2B_TESTING:    return t("itemType.l2bTesting");
    case ITEM_TYPE_SELF_RECORDING: return t("itemType.selfRecording");
    default:                       return "";
  }
}
